import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Button,
  Tooltip,
  IconButton,
} from '@mui/material';
import { Add, Refresh, ArrowBack } from '@mui/icons-material';
import { Consumer, ConsumerStatus, ConsumerPatchRequest, ResumeStrategy } from '@/api/bol-notifications';
import { 
  PageLayout, 
  DataGridContainer, 
  JsonDetailPanel,
} from '@/components';
import { 
  createConsumerColumns,
  defaultConsumerColumnVisibilityModel,
  ConsumerEditDialog,
  ConsumerCreateDialog,
  ConsumerDeleteDialog
} from '@/components/bol-notifications/consumer';

import { 
  useDataGridSelection, 
  usePagination,
  useConsumerManagement,
  usePermissions,
  getActionTooltip
} from '@/hooks';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';

const ConsumersPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { can } = usePermissions();
  
  // Initialize selectedSubscriptionId from URL parameter
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<string>(() => {
    return searchParams.get('subscriptionId') || '';
  });

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // Column visibility state
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(defaultConsumerColumnVisibilityModel);
  
  // Use custom hooks for management
  const {
    consumers,
    subscriptions,
    loading,
    selectedConsumer,
    setSelectedConsumer,
    fetchConsumers,
    fetchSubscriptions,
    createConsumer,
    updateConsumer,
    deleteConsumer,
  } = useConsumerManagement({
    selectedSubscriptionId
  });

  // Use custom hooks for common functionality
  const { 
    selectedItem: selectedConsumerForDetail, 
    selectedRowId,
    handleRowSelectionChange,
    handleCloseDetailPanel 
  } = useDataGridSelection<Consumer>({ 
    data: consumers, 
    getItemId: (item) => item.id || '' 
  });

  const {
    pagination,
    handlePaginationModelChange,
    setPagination,
  } = usePagination();

  // Event handlers
  const handleCreateConsumer = async (data: {
    name: string;
    webhook: {
      url: string;
      method: string;
      headers?: { name: string; value: string }[];
      queryParameters?: { name: string; value: string }[];
    };
    batchCapacity: number;
    batchMinInterval: number;
  }) => {
    const consumerData = {
      name: data.name,
      protocol: 'WEBHOOK' as const,
      webhook: {
        url: data.webhook.url,
        method: data.webhook.method as 'POST',
        ...(data.webhook.headers && data.webhook.headers.length > 0 && { headers: data.webhook.headers }),
        ...(data.webhook.queryParameters && data.webhook.queryParameters.length > 0 && { _queryParameters: data.webhook.queryParameters }),
      },
      batch: {
        capacity: data.batchCapacity,
        minInterval: data.batchMinInterval,
      },
    };
    await createConsumer(consumerData);
    setCreateDialogOpen(false);
    fetchConsumers(pagination.page, pagination.pageSize);
  };

  const handleUpdateConsumer = async (data: {
    name: string;
    status?: ConsumerStatus;
    resumeStrategy?: ResumeStrategy;
    resumeTimestamp?: Date | null;
    webhook: {
      url: string;
      method: string;
      headers?: { name: string; value: string }[];
      queryParameters?: { name: string; value: string }[];
    };
    batchCapacity: number;
    batchMinInterval: number;
  }) => {
    if (!selectedConsumer?.id) return;
    const consumerData: ConsumerPatchRequest = {
      name: data.name,
      status: data.status,
      webhook: {
        url: data.webhook.url,
        method: data.webhook.method as 'POST',
        ...(data.webhook.headers && data.webhook.headers.length > 0 && { headers: data.webhook.headers }),
        // Note: Use _queryParameters (with underscore) to match the OpenAPI generated interface
        ...(data.webhook.queryParameters && data.webhook.queryParameters.length > 0 && { _queryParameters: data.webhook.queryParameters }),
      },
      batch: {
        capacity: data.batchCapacity,
        minInterval: data.batchMinInterval,
      },
    };

    if (data.status === ConsumerStatus.Active && data.resumeStrategy) {
      consumerData.resumeStrategy = data.resumeStrategy;
    }
    if (data.status === ConsumerStatus.Active && data.resumeStrategy === ResumeStrategy.FromTimestamp && data.resumeTimestamp) {
      consumerData.resumeTimestamp = data.resumeTimestamp;
    }

    console.log('Updating consumer with data:', JSON.stringify(consumerData, null, 2));
    console.log('Query parameters from form:', data.webhook.queryParameters);
    await updateConsumer(selectedConsumer.id, consumerData);
    setEditDialogOpen(false);
    setSelectedConsumer(null);
    fetchConsumers(pagination.page, pagination.pageSize);
  };

  const handleDeleteConsumer = async () => {
    if (!selectedConsumer?.id) return;
    await deleteConsumer(selectedConsumer.id);
    setDeleteDialogOpen(false);
    setSelectedConsumer(null);
    fetchConsumers(pagination.page, pagination.pageSize);
  };

  const handleDeleteClick = (consumer: Consumer) => {
    setSelectedConsumer(consumer);
    setDeleteDialogOpen(true);
  };

  const handleEditClick = (consumer: Consumer) => {
    setSelectedConsumer(consumer);
    setEditDialogOpen(true);
  };

  const handleViewProofOfDeliveryClick = (consumer: Consumer) => {
    navigate(`/consumer-proofs-of-delivery?subscriptionId=${selectedSubscriptionId}&consumerId=${consumer.id}&consumerName=${encodeURIComponent(consumer.name)}`);
  };

  const handleSubscriptionChange = (subscriptionId: string) => {
    setSelectedSubscriptionId(subscriptionId);
    setPagination(prev => ({ ...prev, page: 0 }));
  };


  const handleRefresh = () => {
    fetchConsumers(pagination.page, pagination.pageSize);
  };

  // Create columns with actions
  const columns = createConsumerColumns({
    onEdit: handleEditClick,
    onDelete: handleDeleteClick,
    onViewProofOfDelivery: handleViewProofOfDeliveryClick,
    canEdit: can.manageSubscriptions,
    canDelete: can.manageSubscriptions,
    canViewProofOfDelivery: can.manageSubscriptions,
    editTooltip: !can.manageSubscriptions ? getActionTooltip('manageSubscriptions') : '',
    deleteTooltip: !can.manageSubscriptions ? getActionTooltip('manageSubscriptions') : '',
    proofOfDeliveryTooltip: !can.manageSubscriptions ? getActionTooltip('manageSubscriptions') : ''
  });

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  // Auto-select first subscription if no subscription is selected and subscriptions are loaded
  useEffect(() => {
    const subscriptionIdFromUrl = searchParams.get('subscriptionId');
    if (!subscriptionIdFromUrl && subscriptions.length > 0 && !selectedSubscriptionId) {
      // No subscription in URL and no subscription selected -> select first one
      setSelectedSubscriptionId(subscriptions[0].id);
    }
  }, [subscriptions, selectedSubscriptionId, searchParams]);

  useEffect(() => {
    if (selectedSubscriptionId) {
      fetchConsumers(pagination.page, pagination.pageSize);
    }
  }, [selectedSubscriptionId, pagination.page, pagination.pageSize, fetchConsumers]);

  // Update URL when subscription selection changes
  useEffect(() => {
    if (selectedSubscriptionId) {
      setSearchParams({ subscriptionId: selectedSubscriptionId });
    } else {
      setSearchParams({});
    }
  }, [selectedSubscriptionId, setSearchParams]);

  // Update selection when URL parameters change (only on mount or external URL changes)
  useEffect(() => {
    const subscriptionIdFromUrl = searchParams.get('subscriptionId');
    if (subscriptionIdFromUrl && subscriptionIdFromUrl !== selectedSubscriptionId) {
      setSelectedSubscriptionId(subscriptionIdFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const selectedSubscription = subscriptions.find(s => s.id === selectedSubscriptionId);

  return (
    <PageLayout 
      title="Consumers Management"
      selectedItemInfo={selectedConsumerForDetail ? `Selected: ${selectedConsumerForDetail.name}` : undefined}
      leftTitleActions={
        <Tooltip title="Refresh consumers list">
          <IconButton
            onClick={handleRefresh}
            size="small"
          >
            <Refresh />
          </IconButton>
        </Tooltip>
      }
      titleActions={
        <Box sx={{ display: 'flex', gap: 2 }}>
          {searchParams.get('subscriptionId') && (
            <Button 
              onClick={() => navigate('/subscriptions')}
              startIcon={<ArrowBack />}
              variant="contained"
              color="primary"
              size="medium"
            >
              Back to Subscriptions
            </Button>
          )}
          <Tooltip title={
            !can.manageSubscriptions 
              ? getActionTooltip('manageSubscriptions') 
              : (!selectedSubscriptionId ? 'Please select a subscription first' : '')
          }>
            <span>
              <Button
                variant="contained"
                size="medium"
                onClick={() => setCreateDialogOpen(true)}
                startIcon={<Add />}
                disabled={!can.manageSubscriptions || !selectedSubscriptionId}
              >
                Create Consumer
              </Button>
            </span>
          </Tooltip>
        </Box>
      }
      sidePanel={selectedConsumerForDetail ? (
        <JsonDetailPanel
          title="Consumer Details"
          data={selectedConsumerForDetail}
          onClose={handleCloseDetailPanel}
        />
      ) : undefined}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ mb: 2 }}>
          <FormControl sx={{ minWidth: 250, mr: 2 }}>
            <InputLabel>Subscription</InputLabel>
            <Select
              value={selectedSubscriptionId}
              label="Subscription"
              onChange={(e) => handleSubscriptionChange(e.target.value)}
              disabled={subscriptions.length === 0}
            >
              {subscriptions.map((subscription) => (
                <MenuItem key={subscription.id} value={subscription.id}>
                  {subscription.name} ({subscription.memberIdentifier})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedSubscription && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="textSecondary">
                Subscription: <strong>{selectedSubscription.name}</strong> | 
                Type: <Chip label={selectedSubscription.notificationType} size="small" sx={{ ml: 1, mr: 1 }} /> | 
                Resource: <Chip label={selectedSubscription.resourceType} size="small" variant="outlined" />
              </Typography>
            </Box>
          )}

          {!selectedSubscriptionId && subscriptions.length > 0 && (
            <Alert severity="info" icon={<InfoIcon />} sx={{ mt: 2 }}>
              Please select a subscription to view and manage its consumers.
            </Alert>
          )}

          {subscriptions.length === 0 && !loading && (
            <Alert severity="warning" icon={<WarningIcon />} sx={{ mt: 2 }}>
              No subscriptions found. Please create a subscription first.
            </Alert>
          )}
        </Box>

        <DataGridContainer
          rows={consumers}
          columns={columns}
          loading={loading}
          selectedRowId={selectedRowId}
          onRowSelectionChange={handleRowSelectionChange}
          paginationModel={pagination}
          rowCount={pagination.total}
          onPaginationModelChange={handlePaginationModelChange}
          getRowId={(row: { id: string; }) => row.id}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={(model: React.SetStateAction<Record<string, boolean>>) => setColumnVisibilityModel(model)}
          columnOrderStorageKey="consumers-columns"
        />
      </Box>

      {/* Create Consumer Dialog */}
      <ConsumerCreateDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSubmit={handleCreateConsumer}
      />

      {/* Edit Consumer Dialog */}
      {selectedConsumer && (
        <ConsumerEditDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          onSubmit={handleUpdateConsumer}
          consumer={selectedConsumer}
        />
      )}

      <ConsumerDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConsumer}
        consumer={selectedConsumer}
      />

    </PageLayout>
  );
};

export default ConsumersPage;
